import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

export default function ButikkensPersonale() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Butikkens personale - Havneloppen";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Mød det venlige personale bag Havneloppen i Nyborg. Vi står altid klar med et smil, en god historie og hjælp til at finde dit næste loppefund."
      );
    }

    fetch("https://api.dev.auxo.dk/public/content/sider")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const page = data.find((p) => p.url_slug === "personale");
          setPageData(page);
        }
      })
      .catch((err) => console.error("Error fetching staff page data:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-orange" />
      </div>
    );
  }

  const staffImage = pageData?.billede?.[0] || "https://assets.auxodev.dk/assets/19c4efca-c676-4eca-a409-31cc00ad33c0/morten-ejer-havneloppen_1.jpg";
  const optimizedStaffImage = staffImage.replace(
    "assets.auxodev.dk/assets",
    "assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets"
  );

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-brand-blue text-warm-white py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={optimizedStaffImage}
            alt={pageData?.titel || "Butikkens personale"}
            className="h-full w-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-blue via-brand-blue/90 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-orange mb-4">
            <Link to="/" className="hover:text-warm-white transition-colors">FORSIDE</Link>
            <span className="text-stone-400">/</span>
            <span className="text-warm-white">BUTIKKENS PERSONALE</span>
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-warm-white sm:text-4xl md:text-5xl">
            {pageData?.titel || "Butikkens personale"}
          </h1>
        </div>
      </section>

      {/* Content Section */}
      <div className="mx-auto max-w-7xl px-6 py-12 md:py-16 w-full">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start">
        {/* Left: Staff Image */}
        <div className="lg:col-span-5">
          <div className="relative overflow-hidden rounded-2xl border border-warm-beige shadow-md aspect-4/5">
            <img
              src={optimizedStaffImage}
              alt="Morten - Ejer af Havneloppen"
              className="h-full w-full object-cover"
            />
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-brand-blue/90 via-brand-blue/40 to-transparent p-6 text-warm-white">
              <h3 className="font-display text-xl font-bold">Morten</h3>
              <p className="text-xs uppercase tracking-widest text-brand-orange font-semibold">Ejer & Loppeekspert</p>
            </div>
          </div>
        </div>

        {/* Right: Staff Content */}
        <div className="lg:col-span-7 space-y-6">
          <span className="inline-block rounded-full bg-brand-orange/10 border border-brand-orange/20 px-3.5 py-1 text-xs font-bold uppercase tracking-widest text-brand-orange">
            Mød os i butikken
          </span>
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-brand-blue sm:text-4xl">
            {pageData?.titel || "Butikkens personale"}
          </h2>
          <div
            className="prose text-dark-graphite-light"
            dangerouslySetInnerHTML={{ __html: pageData?.indhold || "" }}
          />

          {/* Core Values / Team Spirit */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 pt-6 border-t border-warm-beige">
            <div className="space-y-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                <i className="fa-solid fa-heart text-base" />
              </div>
              <h4 className="font-display text-base font-bold text-brand-blue">Lokal passion</h4>
              <p className="text-xs text-dark-graphite-light leading-relaxed">
                Vi brænder for genbrug, historie og at give gamle sager nyt liv i lokale hjem.
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                <i className="fa-solid fa-comments text-base" />
              </div>
              <h4 className="font-display text-base font-bold text-brand-blue">Altid tid til en snak</h4>
              <p className="text-xs text-dark-graphite-light leading-relaxed">
                Hos os er der ingen stress. Vi elsker en god historie og en hyggelig sludder.
              </p>
            </div>
          </div>

          <div className="pt-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-xl bg-brand-orange px-6 py-3 text-sm font-bold text-warm-white hover:bg-brand-orange-hover transition-colors shadow-sm"
            >
              Kontakt Morten i dag
              <i className="fa-solid fa-arrow-right" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  </div>
);
}
